import { useMemo, useState } from 'react'
import { auroraStore, useAuroraStore } from '../store/auroraStore'
import {
  activeChild, activeCycle, captures, childQuotes, childWork, cycleProgress, dueWeekCheckin,
  planWeeks, principles, reviews, rubricDomains, stalledSkills, weekBlocks, mondayOf,
  type PlanWeek, type Review,
} from '../store/selectors'
import { formatRelative, todayISO } from '../lib/dates'
import { OverTimeChart } from '../components/progress/OverTimeChart'
import { TrackList } from '../components/progress/TrackList'
import { ReviewCard } from '../components/progress/ReviewCard'
import { WeekCheckin } from '../components/checkin/WeekCheckin'
import { CheckinBanner } from '../components/checkin/CheckinBanner'

/**
 * Progress — the loop, seen from above.
 *
 * Where the cycle stands, how each area has moved over time (and where the
 * focus skills are headed), the full list of what we track, and the weekly
 * check-ins with Aurora's reviews. Growth framing only: nothing here compares
 * her to another child, and an unobserved axis reads as "not looked at yet".
 */
export function StoryView() {
  const child = useAuroraStore(activeChild) as any
  const cycle = useAuroraStore(activeCycle) as any
  const weeks = useAuroraStore(planWeeks) as PlanWeek[]
  const domains = useAuroraStore(rubricDomains)
  const stalled = useAuroraStore(stalledSkills)
  const quotes = useAuroraStore(childQuotes)
  const work = useAuroraStore(childWork)
  const recent = useAuroraStore(captures)
  const learned = useAuroraStore(principles)
  const allReviews = useAuroraStore(reviews) as Review[]
  const today = todayISO(child?.timezone)
  const progress = useAuroraStore((s: any) => cycleProgress(s, today))
  const due = useAuroraStore((s: any) => dueWeekCheckin(s, today))
  const thisMonday = mondayOf(today)
  const thisWeek = useAuroraStore((s: any) => weekBlocks(s, thisMonday))
  const [checkinFor, setCheckinFor] = useState<string | null>(null)

  const weekReviews = useMemo(() => allReviews.filter((r) => r.period !== 'day'), [allReviews])
  const weekLabel = (r: Review) => {
    const w = weeks.find((w) => w.startDate && mondayOf(w.startDate) === r.date)
    return w ? `Week ${w.number}${w.theme ? ` · ${w.theme}` : ''}` : `Week of ${r.date}`
  }
  const currentWeek = weeks.find((w) => w.startDate && mondayOf(w.startDate) === thisMonday)
  const weekDone = thisWeek.reduce((n, d) => n + d.blocks.filter((b) => b.status === 'done' || b.status === 'partial').length, 0)
  const weekTotal = thisWeek.reduce((n, d) => n + d.blocks.length, 0)

  const ask = (text: string) => {
    const s = auroraStore.getState() as any
    s.setComposing(text)
    s.setView('thread')
  }

  if (!child) return null
  const anyPicture = domains.some((d) => d.observedCount > 0)

  return (
    <div className="page">
      <header className="page-head reveal">
        <div className="page-eyebrow">Progress</div>
        <h1 className="page-title">How {child.name} is growing</h1>
        {cycle && (
          <p className="page-sub tnum">
            {cycle.title} · week {Math.min(progress.weeksElapsed || 1, progress.weeks || 6)} of {progress.weeks || 6}
            {currentWeek?.theme ? ` · ${currentWeek.theme}` : ''}
            {weekTotal ? ` · ${weekDone} of ${weekTotal} blocks this week` : ''}
          </p>
        )}
      </header>

      <CheckinBanner />

      {!anyPicture ? (
        <div className="empty section">
          <p className="empty-title">No picture yet</p>
          <p className="empty-body">Once a few days are closed and a check-in is in, this page fills with where {child.name} is and how it is moving.</p>
        </div>
      ) : (
        <>
          <section className="section reveal" style={{ '--i': 1 } as React.CSSProperties}>
            <div className="section-head">
              <h2 className="section-title">Over time</h2>
              <button className="btn btn-quiet btn-sm" onClick={() => ask('How is this cycle going against its goals? Where she was, where she is, and whether the targets look on track.')}>
                On track?
              </button>
            </div>
            <OverTimeChart />
          </section>

          <section className="section reveal" style={{ '--i': 2 } as React.CSSProperties}>
            <div className="section-head">
              <h2 className="section-title">What we track</h2>
              <span className="rituals-count tnum">{domains.reduce((n, d) => n + d.observedCount, 0)} skills observed</span>
            </div>
            <TrackList />
          </section>
        </>
      )}

      <section className="section reveal" style={{ '--i': 3 } as React.CSSProperties}>
        <div className="section-head">
          <h2 className="section-title">Check-ins</h2>
          {due ? (
            <button className="btn btn-primary btn-sm" onClick={() => setCheckinFor(due.monday)}>Week {due.week?.number} check-in</button>
          ) : currentWeek ? (
            <button className="btn btn-quiet btn-sm" onClick={() => setCheckinFor(thisMonday)}>
              {allReviews.some((r) => r.period === 'week' && r.date === thisMonday && r.parent?.submittedAt)
                ? "Update this week's check-in"
                : 'Fill in this week early'}
            </button>
          ) : null}
        </div>
        {weekReviews.length === 0 ? (
          <p className="muted" style={{ fontSize: 'var(--text-sm)' }}>
            Every Sunday the week's check-in opens here: a rating per area, the week's questions, the biggest win, and what to adjust. Aurora reviews it and shapes next week.
          </p>
        ) : (
          <div className="stack stack-3">
            {weekReviews.map((r) => <ReviewCard key={r._id} review={r} weekLabel={weekLabel(r)} />)}
          </div>
        )}
      </section>

      {stalled.length > 0 && (
        <section className="section">
          <div className="section-head"><h2 className="section-title">Worth a different approach</h2></div>
          <div className="stack stack-2">
            {stalled.map(({ domain, skill }) => (
              <button key={`${domain}:${skill.key}`} className="stall-card" onClick={() => ask(`${skill.name} hasn't moved. What should we try instead?`)}>
                <span className="stall-card-name">{skill.name}</span>
                <span className="stall-card-note">
                  {skill.note ?? 'Tried repeatedly without movement — probably needs a different approach rather than more practice.'}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {learned.length > 0 && (
        <section className="section">
          <div className="section-head"><h2 className="section-title">What we have learned</h2></div>
          <div className="stack stack-2">
            {learned.slice(0, 6).map((m: any) => (
              <div key={m._id} className="principle">
                <span className="principle-statement">{m.statement}</span>
                {m.detail && <span className="principle-detail">{m.detail}</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      {quotes.length > 0 && (
        <section className="section">
          <div className="section-head"><h2 className="section-title">Her words</h2></div>
          <div className="stack stack-3">
            {quotes.slice(0, 12).map((q, i) => (
              <figure key={`${q.captureId}:${i}`} className="quote">
                <blockquote className="quote-text">“{q.quote}”</blockquote>
                <figcaption className="quote-when">{formatRelative(q.at)}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {work.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h2 className="section-title">Her work</h2>
            {work.length > 1 && (
              <button className="btn btn-quiet btn-sm" onClick={() => ask('Compare her two most recent drawings — what changed?')}>Compare</button>
            )}
          </div>
          <div className="gallery">
            {work.slice(0, 12).map((a: any) => (
              <button key={a._id} className="gallery-item" onClick={() => (auroraStore.getState() as any).selectArtifact(a._id)}>
                {a.storage?.url ? <img src={a.storage.url} alt={a.title} loading="lazy" /> : <span className="gallery-placeholder">{a.title}</span>}
              </button>
            ))}
          </div>
        </section>
      )}

      {recent.length === 0 && anyPicture && (
        <p className="muted section" style={{ fontSize: 'var(--text-sm)' }}>
          Nothing logged recently. Close the day each evening — the notes are the evidence.
        </p>
      )}

      {checkinFor && (
        <WeekCheckin
          monday={checkinFor}
          week={weeks.find((w) => w.startDate && mondayOf(w.startDate) === checkinFor)}
          open={!!checkinFor}
          onClose={() => setCheckinFor(null)}
        />
      )}
    </div>
  )
}
